(() => {
  function attach(gallery, hooks) {
    const grid = gallery.querySelector('[data-qg-feed]'), bank = gallery.querySelector('[data-qg-card-bank]');
    const zone = gallery.querySelector('.qg-load-zone'), pagination = gallery.querySelector('[data-qg-pagination]');
    if (!grid || !bank || !zone) return;
    const more = zone.querySelector('[data-qg-load-more]'), status = zone.querySelector('[data-qg-feed-status]');
    const hint = zone.querySelector('[data-qg-scroll-hint]'), empty = gallery.querySelector('.qg-empty');
    const cards = [], identities = new Set(), sources = new Map();
    let kind = 'all', limit = 0, loading = false, disposed = false, failure = false, revision = 0, round = 0;
    let controller, observer, inside = false;
    const batchSize = () => 5 * Math.max(1, parseInt(getComputedStyle(grid).getPropertyValue('--qg-columns')) || 4);
    function add(nodes) {
      const accepted = nodes.filter(card => {
        const link = card.querySelector('h3 a');
        const id = card.dataset.qgId || card.dataset.discoveryHandle || link?.getAttribute('href');
        const identity = `${card.dataset.qgKind}:${id}`;
        if (!id || identities.has(identity)) return false;
        identities.add(identity); return true;
      });
      const fragment = document.createDocumentFragment();
      accepted.forEach(card => fragment.append(card));
      window.QoolalaCardActions?.refresh(fragment);
      cards.push(...accepted); bank.content.append(fragment);
    }
    add([...grid.children].filter(card => card.matches('.qg-card')));
    if (gallery.classList.contains('qg-feed-home')) {
      const retail = cards.filter(card => card.dataset.qgKind === 'shop');
      const editorial = cards.filter(card => card.dataset.qgKind === 'external');
      cards.length = 0;
      while (retail.length || editorial.length) cards.push(...retail.splice(0, 2), ...editorial.splice(0, 3));
    }
    pagination?.querySelectorAll('[data-qg-next-source]').forEach(link => {
      sources.set(link.dataset.qgNextSource, {link, next: link.getAttribute('href'), seen: new Set()});
    });
    const eligible = () => [...sources.entries()].filter(([source, state]) => state.next &&
      (kind !== 'shop' && kind !== 'external' || kind === source));
    const matching = () => cards.filter(card => card.dataset.qgMediaFailed !== 'true' &&
      (kind === 'all' || card.dataset.qgKind === kind || card.dataset.category?.toUpperCase() === kind ||
       kind === 'saved' && card.dataset.qgSaved === 'true'));
    function render(reset = false, manual = false) {
      const selected = matching(), shown = Math.min(limit, selected.length), remote = eligible().length > 0;
      if (reset) { hooks.before(); [...grid.children].forEach(card => bank.content.append(card)); }
      const added = selected.slice(0, shown).filter(card => card.parentElement !== grid);
      const fragment = document.createDocumentFragment(); added.forEach(card => fragment.append(card)); grid.append(fragment);
      zone.hidden = !selected.length && !remote && !loading;
      more.hidden = shown >= selected.length && !remote;
      more.disabled = loading; more.textContent = loading ? 'Loading finds…' : failure ? 'Try loading again' : 'Load more finds';
      grid.setAttribute('aria-busy', String(loading));
      status.textContent = loading ? 'Loading more finds…' : failure ? 'More finds could not load. Try again or use the next-page links.' :
        remote ? `${shown} finds shown · More to discover` : shown < selected.length ? `${shown} of ${selected.length} finds` : `All ${selected.length} finds shown`;
      hint.hidden = more.hidden || failure;
      empty.hidden = selected.length > 0 || remote || loading;
      if (pagination) pagination.hidden = !failure;
      hooks.after();
      if (manual && added.length) added[0].querySelector('a,button')?.focus({preventScroll: true});
    }
    function pageUrl(raw) {
      const url = new URL(raw, location.href);
      if (url.origin !== location.origin || url.pathname.replace(/\/$/, '') !== location.pathname.replace(/\/$/, '') || url.username || url.password)
        throw new Error('Unexpected feed page');
      url.hash = ''; url.searchParams.set('section_id', gallery.dataset.qgSection);
      return url;
    }
    async function fetchPage(source, state) {
      const url = pageUrl(state.next);
      if (state.seen.has(url.href)) throw new Error('Repeated feed page');
      const request = new AbortController(); controller = request;
      let timedOut = false, response, text;
      const timeout = setTimeout(() => { timedOut = true; request.abort(); }, 15000);
      try {
        response = await fetch(url.href, {credentials: 'same-origin', signal: request.signal});
        if (!response.ok || response.redirected && new URL(response.url).origin !== location.origin) throw new Error('Feed unavailable');
        text = await response.text();
      } catch (error) { if (timedOut) throw new Error('Feed timed out'); throw error; }
      finally { clearTimeout(timeout); }
      if (request.signal.aborted) throw new DOMException('Cancelled', 'AbortError');
      if (text.length > 1500000) throw new Error('Feed page too large');
      const parsed = new DOMParser().parseFromString(text, 'text/html');
      const section = [...parsed.querySelectorAll('[data-qoolala-gallery]')].find(node => node.dataset.qgSection === gallery.dataset.qgSection);
      if (!section?.querySelector('[data-qg-feed]')) throw new Error('Missing feed section');
      const next = [...section.querySelectorAll('[data-qg-next-source]')].find(node => node.dataset.qgNextSource === source)?.getAttribute('href');
      const incoming = [...section.querySelector('[data-qg-feed]').children].filter(card =>
        card.matches('.qg-card') && card.dataset.qgKind === source);
      // Import cards only. Never run scripts returned with the section shell.
      incoming.forEach(card => {
        card.querySelectorAll('script,iframe,object,embed').forEach(node => node.remove());
        [card, ...card.querySelectorAll('*')].forEach(node => [...node.attributes].forEach(attr => {
          if (/^on/i.test(attr.name)) node.removeAttribute(attr.name);
        }));
      });
      add(incoming.map(card => document.importNode(card, true)));
      state.seen.add(url.href); state.next = next || null;
      if (next) state.link.href = next; else state.link.hidden = true;
    }
    async function load(manual = false) {
      if (disposed || loading || failure && !manual) return;
      const current = revision, target = limit + batchSize();
      loading = true; failure = false; render();
      try {
        // Bound each gesture when pages contain only filtered/unapproved records.
        for (let reads = 0; matching().length < target && eligible().length && reads < 4; reads++) {
          const available = eligible(), [source, state] = available[round++ % available.length];
          await fetchPage(source, state);
          if (disposed || current !== revision) return;
        }
        limit = target;
      } catch (error) { if (error.name !== 'AbortError') failure = true; }
      finally { loading = false; if (!disposed) render(false, manual && current === revision); }
    }
    more.addEventListener('click', () => load(true));
    gallery.addEventListener('qoolala:saved', () => { if (kind === 'saved') render(true); });
    limit = batchSize(); render(true);
    if ('IntersectionObserver' in window && !navigator.connection?.saveData) {
      observer = new IntersectionObserver(entries => entries.forEach(entry => {
        const entering = entry.isIntersecting && !inside; inside = entry.isIntersecting;
        if (entering && (limit < matching().length || eligible().length)) load();
      }), {rootMargin: '0px 0px 180px 0px', threshold: 0}); observer.observe(zone);
    } else hint.textContent = 'Load more to keep exploring.';
    const cancel = () => controller?.abort();
    window.addEventListener('pagehide', cancel);
    function cleanup(event) {
      if (!event.target.contains(gallery)) return;
      disposed = true; cancel(); observer?.disconnect(); window.removeEventListener('pagehide', cancel);
      document.removeEventListener('shopify:section:unload', cleanup);
    }
    document.addEventListener('shopify:section:unload', cleanup);
    return {refresh() { render(true); }, filter(value) {
      kind = value; revision++; cancel(); limit = batchSize(); inside = false; render(true);
      observer?.unobserve(zone); observer?.observe(zone);
    }};
  }
  window.QoolalaFeedLoader = {attach};
})();
